import {appConfig} from '@/config/appConfig';
import {useLocaleStore} from '@/stores/localeStore';

export const DESKLY_BASE_URL = 'https://desk.ly';

// Builds a URL on the public desk.ly site; a blank locale falls back to English.
export function desklyUrl(path = '', locale = 'en'): string {
    const localeSegment = locale || 'en';
    const pathSegment = path ? `/${path.replace(/^\/+/, '')}` : '';

    return `${DESKLY_BASE_URL}/${localeSegment}${pathSegment}`;
}

// Pages that only exist in English and German; any other locale resolves to English.
function toSupportedLocale(locale: string): 'de' | 'en' {
    return locale === 'de' ? 'de' : 'en';
}

// Links whose German and English variants are separate, hand-translated slugs rather
// than the same path under a different locale prefix.
function localizedPath(locale: string, dePath: string, enPath: string): string {
    return `${DESKLY_BASE_URL}/${toSupportedLocale(locale) === 'de' ? dePath : enPath}`;
}

/**
 * Named registry of every public desk.ly link used in the app. Each entry owns its
 * locale behaviour so components never build these URLs or branch on the locale inline:
 * - `deskLyUrl(slug, locale)` keeps the raw locale segment (pages exist per locale).
 * - `deskLyUrl(slug, toSupportedLocale(locale))` clamps to en/de (pages exist only there).
 * - `localizedPath(...)` picks between fully distinct German and English slugs.
 */
const DESKLY_LINKS = {
    siteRoot: () => `${DESKLY_BASE_URL}/`,
    thirdPartyCopyright: () => `${DESKLY_BASE_URL}/third-party-copyright-information`,

    home: locale => desklyUrl('', toSupportedLocale(locale)),
    legal: locale => desklyUrl('legal', toSupportedLocale(locale)),
    privacy: locale => desklyUrl('privacy', toSupportedLocale(locale)),
    terms: locale => desklyUrl('terms', toSupportedLocale(locale)),
    help: locale => desklyUrl('help', toSupportedLocale(locale)),
    helpLegacyICal: locale => desklyUrl('help-legacyical', toSupportedLocale(locale)),
    registrationThankYou: locale =>
        toSupportedLocale(locale) === 'de'
            ? desklyUrl('danke-f%C3%BCr-die-registrierung', 'de')
            : desklyUrl('thank-you-for-your-registration', 'en'),

    helpAnalytics: locale => desklyUrl('help-analytics', locale),
    rightsAndRoles: locale => desklyUrl('rights-and-roles', locale),
    helpCalendarIntegrationGoogle: locale => desklyUrl('help-calint-google', locale),

    parkingProduct: locale =>
        localizedPath(
            locale,
            'de/produkt/parkplatz-buchungssystem',
            'en/product/parking-space-booking'
        ),
    integrationsProduct: locale =>
        localizedPath(locale, 'de/produkt/integrationen', 'en/product/integrations'),
    desklyConnectProduct: locale =>
        localizedPath(locale, 'upgrade/deskly-connect', 'en/upgrade/deskly-connect'),
    priorityBookingUpgrade: locale =>
        localizedPath(locale, 'upgrade/priorisiertes-buchen', 'en/upgrade/priority-booking'),
    apiInterfaceUpgrade: locale =>
        localizedPath(locale, 'upgrade/api-schnittstelle', 'en/upgrade/api-interface'),
    calendarSyncUpgrade: locale =>
        localizedPath(locale, 'upgrade/kalender-sync', 'en/upgrade/calendar-sync'),
    visitorManagementUpgrade: locale =>
        localizedPath(locale, 'upgrade/besuchermanagement', 'en/upgrade/besuchermanagement'),
    waitingListHelp: locale =>
        localizedPath(
            locale,
            'help/wie-nutze-ich-wartelisten-f%C3%BCr-pl%C3%A4tze-und-zonen',
            'en/help/how-do-i-use-the-waiting-list-for-desks-or-zones'
        ),
    spaceImagesHelp: locale =>
        localizedPath(
            locale,
            'help/wie-fuege-ich-bilder-zum-raumplan-hinzu',
            'en/help/how-to-add-images-to-floorplans-and-locations'
        ),
    customUserTagsHelp: locale =>
        localizedPath(
            locale,
            'help/wie-f%C3%BCge-ich-individuelle-user-tags-hinzu',
            'en/help/how-do-i-add-custom-user-tags'
        ),
    occupancyAnalysisHelp: locale =>
        localizedPath(
            locale,
            'help/wie-analysiere-ich-die-auslastung-meiner-ressourcen',
            'en/help/how-to-analyse-office-occupancy'
        ),
    manageUserTagsHelp: locale =>
        localizedPath(locale, 'help/wie-weise-ich-user-tags-zu', 'en/help/how-to-manage-user-tags'),
    bookingRulesHelp: locale =>
        localizedPath(locale, 'help/buchungsregeln-erstellen', 'en/help/booking-rules'),
    expertCall: locale => localizedPath(locale, 'de/kennenlerntermin', 'en/meet-an-expert'),

    // Help center and licensing funnel; these keep the raw locale segment.
    managerRolesHelp: locale => desklyUrl('help-roles-manager', locale),
    ssoHelp: locale => desklyUrl('help-sso', locale),
    getAQuote: locale => desklyUrl('get-a-quote', locale),
    licenseForm: locale => desklyUrl('license-form', locale),
    featurebasePrivacy: locale => desklyUrl('privacy', locale),
    landingPage: locale => desklyUrl('', locale),
    dataProtection: locale => desklyUrl('data-protection', toSupportedLocale(locale)),
    desklyConnectInfo: locale => desklyUrl('info-deskly-connect', toSupportedLocale(locale)),
    helpCenter: () => `${DESKLY_BASE_URL}/help`,
} satisfies Record<string, (locale: string) => string>;

export type DesklyLinkName = keyof typeof DESKLY_LINKS;

// Resolves a named public desk.ly link for the active locale from the locale store.
export function desklyLink(name: DesklyLinkName): string {
    return DESKLY_LINKS[name](useLocaleStore().currentLocale);
}

export function redirectIfRootDomain(): void {
    if (__IS_HYBRID_APP__) {
        return;
    }
    const hostname = window.location.hostname; // e.g., "sub.example.com"
    const parts = hostname.split('.');

    // Handle localhost separately
    if (hostname === 'localhost') return;

    // Only redirect if on the root domain (e.g. desk.ly), not a subdomain (e.g. app.desk.ly)
    if (parts.length <= 2) {
        const {protocol, port, pathname, search, hash} = window.location;
        const portSuffix = port ? `:${port}` : '';
        window.location.replace(
            `${protocol}//app.${hostname}${portSuffix}${pathname}${search}${hash}`
        );
    }
}

const SAFE_HREF_PROTOCOLS = ['http:', 'https:'];

// Blocks javascript:/data: schemes when a user-supplied url is interpolated into an href.
export function safeHref(url: string): string {
    try {
        return SAFE_HREF_PROTOCOLS.includes(new URL(url).protocol) ? url : '#';
    } catch {
        return '#';
    }
}

/**
 * Returns the image url directly if not in hybrid app context.
 * Otherwise returns the full url (including baseUrl) if the image url is relative.
 */
export function getNormalizedImageUrl(imageUrl: string | null | undefined): string | null {
    if (!__IS_HYBRID_APP__) {
        return imageUrl ?? null;
    }

    if (!imageUrl) {
        return null;
    }

    if (imageUrl.trim().startsWith('http')) {
        return imageUrl;
    }

    return `${appConfig.baseUrl}${imageUrl}`;
}
